import { useEffect, useState } from 'react'
import { httpRequest } from '../lib/http.js'

const SYNC_INTERVAL_MS = 10_000
const MAX_ACCEPTED_RTT_MS = 2_000
export const SERVER_CLOCK_FAILURE_GRACE_MS = 30_000

export interface ServerClockSnapshot {
  now: Date
  nowMs: number
  offsetMs: number
  rttMs: number | null
  syncedAt: number | null
  serverIso: string | null
  serverTimezone: string | null
  status: 'SYNCING' | 'SYNCED' | 'ERROR'
}

interface TimeResponse {
  serverTime: number
  serverIso?: string
  serverTimezone?: string
  sentAt?: number
}

let offsetMs = 0
let rttMs: number | null = null
let syncedAt: number | null = null
let serverIso: string | null = null
let serverTimezone: string | null = null
let status: ServerClockSnapshot['status'] = 'SYNCING'
let syncTimer: ReturnType<typeof setInterval> | null = null
let tickTimer: ReturnType<typeof setInterval> | null = null
let subscriberCount = 0
let syncFailureCount = 0
let nextSyncAllowedAt = 0
const listeners = new Set<() => void>()

function snapshot(): ServerClockSnapshot {
  const nowMs = Date.now() + offsetMs
  return {
    now: new Date(nowMs),
    nowMs,
    offsetMs,
    rttMs,
    syncedAt,
    serverIso,
    serverTimezone,
    status,
  }
}

function notify() {
  listeners.forEach(listener => listener())
}

/** Une mesure manquée ne périme pas instantanément une ancre encore récente. */
export function serverClockStatusAfterFailure(lastSyncedAt: number | null, failedAt: number): ServerClockSnapshot['status'] {
  if (lastSyncedAt === null) return 'ERROR'
  return failedAt - lastSyncedAt < SERVER_CLOCK_FAILURE_GRACE_MS ? 'SYNCED' : 'ERROR'
}

async function syncOnce() {
  if (Date.now() < nextSyncAllowedAt) return
  const clientSentAt = Date.now()
  try {
    const data = await httpRequest<TimeResponse>(`/time?clientSentAt=${clientSentAt}`, { cache: 'no-store' })
    const clientReceivedAt = Date.now()
    const serverSentAt = data.sentAt ?? data.serverTime
    const roundTrip = clientReceivedAt - clientSentAt

    // Estimation NTP simple : on considère que le serveur a répondu au milieu
    // du trajet réseau. En local, le RTT est normalement très faible.
    const estimatedClientAtServerSend = clientSentAt + roundTrip / 2
    const nextOffsetMs = serverSentAt - estimatedClientAtServerSend

    // On ignore les mesures avec une latence anormale pour éviter un saut
    // visuel de l'horloge si le navigateur ou la machine freeze brièvement.
    if (roundTrip <= MAX_ACCEPTED_RTT_MS) {
      offsetMs = nextOffsetMs
      rttMs = roundTrip
      syncedAt = clientReceivedAt
      serverIso = data.serverIso ?? null
      serverTimezone = data.serverTimezone ?? null
      status = 'SYNCED'
      syncFailureCount = 0
      nextSyncAllowedAt = 0
      notify()
    } else {
      syncFailureCount += 1
      nextSyncAllowedAt = Date.now() + SYNC_INTERVAL_MS
      status = serverClockStatusAfterFailure(syncedAt, Date.now())
      notify()
    }
  } catch (err) {
    syncFailureCount += 1
    nextSyncAllowedAt = Date.now() + Math.min(20_000, SYNC_INTERVAL_MS * 2 ** Math.min(syncFailureCount, 3))
    // L'ancre monotone reste assez précise pendant une micro-coupure. Elle ne
    // devient indisponible qu'après la fenêtre de grâce, afin qu'un seul GET
    // /time manqué ne vide pas toute la console opérateur.
    status = serverClockStatusAfterFailure(syncedAt, Date.now())
    notify()
  }
}

function ensureStarted() {
  if (!syncTimer) {
    void syncOnce()
    syncTimer = setInterval(() => { void syncOnce() }, SYNC_INTERVAL_MS)
  }
  if (!tickTimer) {
    tickTimer = setInterval(notify, 100)
  }
}

function maybeStop() {
  if (subscriberCount > 0) return
  if (syncTimer) clearInterval(syncTimer)
  if (tickTimer) clearInterval(tickTimer)
  syncTimer = null
  tickTimer = null
}

export function getServerNowMs(): number {
  return Date.now() + offsetMs
}

export function getServerNow(): Date {
  return new Date(getServerNowMs())
}

export function useServerClock(): ServerClockSnapshot {
  const [state, setState] = useState(() => snapshot())

  useEffect(() => {
    subscriberCount++
    const listener = () => setState(snapshot())
    listeners.add(listener)
    ensureStarted()
    listener()

    return () => {
      listeners.delete(listener)
      subscriberCount = Math.max(0, subscriberCount - 1)
      maybeStop()
    }
  }, [])

  return state
}
